/**
 * Seller Document Upload Component
 * 
 * Allows sellers to upload required documents for AML compliance:
 * - Proof of Ownership (deed, mortgage statement)
 * - Photo ID (passport, driving licence)
 * - Proof of Address (utility bill, council tax)
 * - EPC Certificate (energy performance)
 */

import React, { useState } from "react";
import { Upload, CheckCircle, AlertCircle, Loader } from "lucide-react";

const REQUIRED_DOCUMENTS = [
  {
    type: "PROOF_OF_OWNERSHIP",
    label: "Proof of Ownership",
    description: "Deed, mortgage statement, or property registration",
    icon: "📄"
  },
  {
    type: "PHOTO_ID",
    label: "Photo ID",
    description: "Passport or driving licence (clear photo of both sides)",
    icon: "🪪"
  },
  {
    type: "PROOF_OF_ADDRESS",
    label: "Proof of Address",
    description: "Recent utility bill or council tax document",
    icon: "🏠"
  },
  {
    type: "EPC_CERTIFICATE",
    label: "EPC Certificate",
    description: "Energy Performance Certificate (optional but helpful)",
    icon: "⚡",
    optional: true
  }
];

export function SellerDocumentUpload({
  onboardingId,
  onUploadsComplete,
  maxFileSize = 10 * 1024 * 1024
}) {
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const handleFileSelect = (e, docType) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size
    if (file.size > maxFileSize) {
      alert(
        `File too large. Maximum size is ${Math.round(maxFileSize / (1024 * 1024))}MB`
      );
      return;
    }

    // Validate file type
    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "image/webp"
    ];
    if (!allowedTypes.includes(file.type)) {
      alert("Invalid file type. Please upload PDF, JPG, PNG, or WEBP");
      return;
    }

    // Add or replace document
    setDocuments((prev) => [
      ...prev.filter((d) => d.type !== docType),
      {
        type: docType,
        file,
        status: "pending"
      }
    ]);
    setUploadError(null);
  };

  const uploadDocuments = async () => {
    if (documents.length === 0) {
      setUploadError("Please upload at least the required documents");
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      for (const doc of documents) {
        if (doc.status !== "pending") continue;

        // Update status to uploading
        setDocuments((prev) =>
          prev.map((d) =>
            d.type === doc.type ? { ...d, status: "uploading" } : d
          )
        );

        // Create form data
        const formData = new FormData();
        formData.append("file", doc.file);
        formData.append("category", doc.type);
        formData.append("description", `Seller onboarding - ${doc.type}`);
        formData.append("entityType", "SELLER_ONBOARDING");
        formData.append("entityId", onboardingId);

        // Upload to /api/documents/upload
        const response = await fetch("/api/documents/upload", {
          method: "POST",
          body: formData
        });

        if (!response.ok) {
          const error = await response.json();
          setDocuments((prev) =>
            prev.map((d) =>
              d.type === doc.type
                ? {
                    ...d,
                    status: "error",
                    error: error.error || "Upload failed"
                  }
                : d
            )
          );
        } else {
          // Success
          setDocuments((prev) =>
            prev.map((d) =>
              d.type === doc.type
                ? { ...d, status: "success", uploadedAt: new Date() }
                : d
            )
          );
        }
      }

      // Check if all required documents uploaded successfully
      const requiredDocs = REQUIRED_DOCUMENTS.filter((d) => !d.optional);
      const uploadedRequired = documents
        .filter((d) => requiredDocs.some((r) => r.type === d.type))
        .filter((d) => d.status === "success" || d.uploadedAt);

      if (uploadedRequired.length === requiredDocs.length) {
        onUploadsComplete?.();
      }
    } catch (error) {
      setUploadError(
        error instanceof Error ? error.message : "Upload failed"
      );
    } finally {
      setUploading(false);
    }
  };

  const uploadedCount = documents.filter(
    (d) => d.status === "success" || d.uploadedAt
  ).length;
  const requiredCount = REQUIRED_DOCUMENTS.filter((d) => !d.optional).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-semibold text-white mb-2">
          Upload Required Documents
        </h2>
        <p className="text-white/70">
          Complete AML compliance verification. All documents are securely stored
          and encrypted.
        </p>
      </div>

      {/* Progress */}
      <div className="bg-white/5 rounded-xl border border-white/10 p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-white">
            Uploaded: {uploadedCount} of {requiredCount} required
          </span>
          <div className="w-32 h-2 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 transition-all duration-300"
              style={{ width: `${(uploadedCount / requiredCount) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Error Message */}
      {uploadError && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-red-300">Upload Error</p>
            <p className="text-sm text-red-200/80">{uploadError}</p>
          </div>
        </div>
      )}

      {/* Document Upload Cards */}
      <div className="grid gap-4 md:grid-cols-2">
        {REQUIRED_DOCUMENTS.map((docDef) => {
          const doc = documents.find((d) => d.type === docDef.type);
          const isOptional = docDef.optional;

          return (
            <div
              key={docDef.type}
              className={`rounded-xl border p-4 transition ${
                doc?.status === "success"
                  ? "border-emerald-500/30 bg-emerald-500/5"
                  : doc?.status === "error"
                  ? "border-red-500/30 bg-red-500/5"
                  : "border-white/10 bg-white/5 hover:border-white/20"
              }`}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="text-lg font-medium text-white flex items-center gap-2">
                    <span>{docDef.icon}</span>
                    {docDef.label}
                    {isOptional && (
                      <span className="text-xs bg-amber-500/20 text-amber-300 px-2 py-1 rounded">
                        Optional
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-white/60 mt-1">
                    {docDef.description}
                  </p>
                </div>

                {doc?.status === "success" ? (
                  <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                ) : doc?.status === "error" ? (
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                ) : null}
              </div>

              {/* File Input / Status */}
              {!doc || doc.status === "error" ? (
                <label className="block cursor-pointer">
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.webp"
                    onChange={(e) => handleFileSelect(e, docDef.type)}
                    disabled={uploading}
                    className="hidden"
                  />
                  <div className="border-2 border-dashed border-white/20 rounded-lg p-4 hover:border-white/40 transition text-center">
                    <Upload className="w-5 h-5 text-white/60 mx-auto mb-2" />
                    <p className="text-sm text-white/80">
                      Click to upload or drag file here
                    </p>
                    <p className="text-xs text-white/60 mt-1">
                      PDF, JPG, PNG, WEBP (max 10MB)
                    </p>
                  </div>
                </label>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-white/80 truncate">
                      {doc.file.name}
                    </span>
                    <span className="text-white/60">
                      {(doc.file.size / 1024 / 1024).toFixed(1)}MB
                    </span>
                  </div>

                  {doc.status === "uploading" && (
                    <div className="flex items-center gap-2">
                      <Loader className="w-4 h-4 text-blue-400 animate-spin" />
                      <span className="text-xs text-blue-300">Uploading...</span>
                    </div>
                  )}

                  {doc.status === "success" && (
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      <span className="text-xs text-emerald-300">
                        Uploaded {doc.uploadedAt?.toLocaleDateString() || ""}
                      </span>
                    </div>
                  )}

                  {doc.status === "error" && (
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-red-400" />
                      <span className="text-xs text-red-300">{doc.error}</span>
                    </div>
                  )}

                  {doc.status !== "uploading" && (
                    <label className="block cursor-pointer">
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.webp"
                        onChange={(e) => handleFileSelect(e, docDef.type)}
                        disabled={uploading}
                        className="hidden"
                      />
                      <p className="text-xs text-white/60 hover:text-white/80">
                        Replace file
                      </p>
                    </label>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Upload Button */}
      <button
        onClick={uploadDocuments}
        disabled={uploading || documents.length === 0}
        className={`w-full py-3 px-4 rounded-xl font-medium transition ${
          uploading || documents.length === 0
            ? "bg-white/10 text-white/60 cursor-not-allowed"
            : "bg-emerald-600 text-white hover:bg-emerald-700"
        }`}
      >
        {uploading ? (
          <span className="flex items-center justify-center gap-2">
            <Loader className="w-4 h-4 animate-spin" />
            Uploading...
          </span>
        ) : (
          `Upload ${documents.length} Document${documents.length !== 1 ? "s" : ""}`
        )}
      </button>

      {/* Info Box */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
        <p className="text-sm text-blue-200">
          <strong>Why we need these documents:</strong> These documents help us verify your
          identity and right to sell the property, fulfilling AML (Anti-Money Laundering)
          compliance requirements. Your documents are encrypted and stored securely.
        </p>
      </div>
    </div>
  );
}
